import React, { useEffect } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTheme } from '../../theme/theme-provider';
import { useNavigate } from 'react-router-dom';
import ThemeAmbientOverlay from '../theme-ambient-overlay/ThemeAmbientOverlay';

const serviceItems = [
  {
    title: 'Custom Music',
    body: 'Original music for brands, films, games, and digital experiences.',
    buttons: [
      { label: 'DISCUSS A PROJECT', href: '/contact/' },
      { label: 'GAME MUSIC ON FIVERR ↗', href: 'https://www.fiverr.com/s/2pGkqBL', external: true }
    ]
  },
  {
    title: 'Sound Design',
    body: 'Game audio, sound effects, product sounds, and sonic assets.',
    buttons: [
      { label: 'DISCUSS A PROJECT', href: '/contact/' }
    ]
  },
  {
    title: 'Sonic Branding',
    body: 'Audio logos, brand anthems, and sonic identities.',
    buttons: [
      { label: 'DISCUSS A PROJECT', href: '/contact/' }
    ]
  },
  {
    title: 'Mixing & Mastering',
    body: 'Professional mixing and mastering for Drum & Bass, electronic and cinematic music.',
    buttons: [
      { label: 'DISCUSS A PROJECT', href: '/contact/' },
      { label: 'ELECTRONIC & CINEMATIC ON FIVERR ↗', href: 'https://www.fiverr.com/s/61b0Dqq', external: true },
      { label: 'DRUM & BASS ON FIVERR ↗', href: 'https://www.fiverr.com/s/8xdY8Aq', external: true }
    ]
  }
];

const Services = ({ sendPage }) => {
  const { themeMode } = useTheme();
  const navigate = useNavigate();
  const isDark = themeMode === 'dark';
  const accentGold = isDark ? '#F2D399' : '#1f1f1f';

  useEffect(() => {
    sendPage('services');
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: 'var(--background-color)',
        minHeight: '100vh',
        pt: { xs: 2, md: 2 },
        pb: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <ThemeAmbientOverlay speed={30} />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <Typography
          align="center"
          variant="h2"
          color={isDark ? 'white' : 'rgba(22,22,22,0.94)'}
          sx={{
            fontFamily: 'Light-1',
            textShadow: isDark ? '4px 4px black' : '2px 2px rgba(255,255,255,0.65)',
            fontSize: { xs: '2.4rem', sm: '3rem', md: '3.5rem' },
            mb: 2
          }}
        >
          Services
        </Typography>

        <Stack spacing={2.5}>
          {serviceItems.map((item) => (
            <Box
              key={item.title}
              sx={{
                position: 'relative',
                border: isDark ? '1px solid rgba(242, 211, 153, 0.25)' : '1px solid rgba(0,0,0,0.1)',
                boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.25)' : '0 10px 24px rgba(0,0,0,0.12)',
                borderRadius: 2,
                p: { xs: 2.5, md: 3 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: isDark
                    ? 'linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.8) 15%, rgba(0,0,0,0.8) 85%, rgba(0,0,0,0.05) 100%)'
                    : 'linear-gradient(90deg, rgba(255,250,244,0.1) 0%, rgba(255,250,244,0.92) 15%, rgba(255,250,244,0.92) 85%, rgba(255,250,244,0.1) 100%)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 2,
                  zIndex: -1
                }
              }}
            >
              <Typography
                variant="h5"
                color={accentGold}
                sx={{
                  fontFamily: '"Courier New", monospace',
                  fontWeight: 'bold',
                  mb: 1
                }}
              >
                {item.title}
              </Typography>
              <Typography
                color={isDark ? 'white' : 'rgba(0,0,0,0.82)'}
                sx={{
                  fontFamily: '"Courier New", monospace',
                  fontWeight: 600,
                  lineHeight: 1.7
                }}
              >
                {item.body}
              </Typography>

              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                {item.buttons.map((btn) =>
                  btn.external ? (
                    <Button
                      key={btn.label}
                      component="a"
                      href={btn.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="small"
                      sx={{
                        fontFamily: '"Courier New", monospace',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.08em',
                        borderColor: isDark ? 'rgba(242,211,153,0.5)' : 'rgba(0,0,0,0.35)',
                        color: isDark ? '#F2D399' : 'rgba(0,0,0,0.75)',
                        '&:hover': {
                          borderColor: isDark ? '#F2D399' : 'rgba(0,0,0,0.75)',
                          backgroundColor: isDark ? 'rgba(242,211,153,0.08)' : 'rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      {btn.label}
                    </Button>
                  ) : (
                    <Button
                      key={btn.label}
                      onClick={() => navigate(btn.href)}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontFamily: '"Courier New", monospace',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.08em',
                        borderColor: isDark ? 'rgba(242,211,153,0.5)' : 'rgba(0,0,0,0.35)',
                        color: isDark ? '#F2D399' : 'rgba(0,0,0,0.75)',
                        '&:hover': {
                          borderColor: isDark ? '#F2D399' : 'rgba(0,0,0,0.75)',
                          backgroundColor: isDark ? 'rgba(242,211,153,0.08)' : 'rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      {btn.label}
                    </Button>
                  )
                )}
              </Stack>
            </Box>
          ))}
        </Stack>


      </Container>
    </Box>
  );
};

export default Services;